import type { PokerBlindMarker, PokerBlindMarkerKind, PokerSeat, PokerState } from "../types";

export const POKER_BLIND_MARKER_KINDS: readonly PokerBlindMarkerKind[] = ["small", "big"];

const finiteClamp = (value: number, minimum: number, maximum: number) => Math.max(minimum, Math.min(maximum, Number.isFinite(value) ? value : minimum));

export function createPokerBlindMarkers(now = Date.now()): Record<PokerBlindMarkerKind, PokerBlindMarker> {
  return {
    small: { kind: "small", x: .44, y: .76, rotation: 0, movedAt: now },
    big: { kind: "big", x: .50, y: .76, rotation: 0, movedAt: now },
  };
}

/** Supplies stable defaults for poker scenes saved before blind markers existed. */
export function normalizedPokerBlindMarkers(state: Pick<PokerState, "blindMarkers" | "updatedAt">): Record<PokerBlindMarkerKind, PokerBlindMarker> {
  const defaults = createPokerBlindMarkers(state.updatedAt);
  return {
    small: normalizeMarker(state.blindMarkers?.small, defaults.small),
    big: normalizeMarker(state.blindMarkers?.big, defaults.big),
  };
}

function normalizeMarker(marker: PokerBlindMarker | undefined, fallback: PokerBlindMarker): PokerBlindMarker {
  if (!marker) return fallback;
  return {
    ...marker,
    kind: fallback.kind,
    x: finiteClamp(marker.x, .06, .94),
    y: finiteClamp(marker.y, .09, .91),
    rotation: Number.isFinite(marker.rotation) ? marker.rotation : fallback.rotation,
    movedAt: Number.isFinite(marker.movedAt) ? marker.movedAt : fallback.movedAt,
  };
}

/** Places each blind disc just inside its assigned player's rail position. */
export function pokerBlindMarkerSeatPosition(seat: Pick<PokerSeat, "edge" | "slot">, kind: PokerBlindMarkerKind) {
  const slot = seat.slot === "left" ? -1 : seat.slot === "right" ? 1 : 0;
  const tangent = kind === "small" ? -.025 : .025;
  if (seat.edge === "north") return { x: .5 + slot * .25 + tangent, y: .19, rotation: Math.PI };
  if (seat.edge === "south") return { x: .5 - slot * .25 + tangent, y: .81, rotation: 0 };
  if (seat.edge === "east") return { x: .81, y: .5 + slot * .25 + tangent, rotation: -Math.PI / 2 };
  return { x: .19, y: .5 - slot * .25 + tangent, rotation: Math.PI / 2 };
}

export function movePokerBlindMarker(
  state: PokerState,
  kind: PokerBlindMarkerKind,
  target: { x: number; y: number; rotation?: number },
  now = Date.now(),
  seatId?: string,
): PokerState {
  const markers = normalizedPokerBlindMarkers(state); const current = markers[kind];
  const next: PokerBlindMarker = {
    kind,
    seatId,
    x: finiteClamp(target.x, .06, .94),
    y: finiteClamp(target.y, .09, .91),
    rotation: Number.isFinite(target.rotation) ? target.rotation! : current.rotation,
    movedAt: now,
    motion: { fromX: current.x, fromY: current.y, fromRotation: current.rotation, startedAt: now },
  };
  return { ...state, blindMarkers: { ...markers, [kind]: next }, updatedAt: now };
}

export function assignPokerBlindMarker(state: PokerState, kind: PokerBlindMarkerKind, seatId?: string, now = Date.now()): PokerState {
  const seat = seatId ? state.seats.find((candidate) => candidate.id === seatId) : undefined;
  if (seatId && !seat) return state;
  const current = normalizedPokerBlindMarkers(state)[kind];
  return movePokerBlindMarker(state, kind, seat ? pokerBlindMarkerSeatPosition(seat, kind) : current, now, seat?.id);
}

export function setPokerHandNumber(state: PokerState, handNumber: number, now = Date.now()): PokerState {
  const nextHandNumber = Math.round(finiteClamp(handNumber, 1, 9_999));
  if (nextHandNumber === state.handNumber) return state;
  return { ...state, handNumber: nextHandNumber, updatedAt: now };
}

export function resetPokerHandNumber(state: PokerState, now = Date.now()) {
  return setPokerHandNumber(state, 1, now);
}
